import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { SERVICE_OPTIONS } from "../../data/content";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import WhatsAppIcon from "./WhatsAppIcon";
import { waLink } from "../../lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", company: "", service: "", origin: "", destination: "", date: "", details: "" };

export default function QuoteForm({ tid = "quote", className = "", onSuccess }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please fill in your name, email and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received! Our team will reach out the same day.");
      setSent({ name: form.name, service: form.service, origin: form.origin, destination: form.destination });
      setForm(empty);
      onSuccess?.();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    const route = sent.origin && sent.destination ? `${sent.origin} → ${sent.destination}` : "";
    const summary = [sent.service, route].filter(Boolean).join(", ");
    const msg = `Hi Conexa Relay! I just submitted a quote request${sent.name ? ` (${sent.name})` : ""}${summary ? ` — ${summary}` : ""}. Could you follow up?`;
    return (
      <div data-testid={`${tid}-success`} className={`text-center space-y-5 ${className}`}>
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
          <Check className="w-7 h-7" />
        </div>
        <div>
          <h3 className="font-heading font-extrabold text-2xl tracking-tight">Request received{sent.name ? `, ${sent.name.split(" ")[0]}` : ""}!</h3>
          <p className="mt-2 text-muted-foreground text-sm max-w-sm mx-auto">
            Our dispatch team will get back to you within hours. Want an answer even faster? Continue on WhatsApp and we'll pick it up right away.
          </p>
        </div>
        <a
          data-testid={`${tid}-wa-continue`}
          href={waLink(msg)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-semibold py-4 hover:brightness-110 transition-all"
        >
          <WhatsAppIcon className="w-5 h-5" /> Continue on WhatsApp
        </a>
        <button
          type="button"
          data-testid={`${tid}-reset`}
          onClick={() => setSent(null)}
          className="text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${tid}-form`} className={`space-y-4 ${className}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid={`${tid}-name`} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-background/50 h-12" />
        <Input data-testid={`${tid}-email`} type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-background/50 h-12" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid={`${tid}-phone`} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-background/50 h-12" />
        <Input data-testid={`${tid}-company`} placeholder="Company" value={form.company} onChange={(e) => set("company", e.target.value)} className="bg-background/50 h-12" />
      </div>
      <Select value={form.service} onValueChange={(v) => set("service", v)}>
        <SelectTrigger data-testid={`${tid}-service`} className="bg-background/50 h-12"><SelectValue placeholder="Service needed *" /></SelectTrigger>
        <SelectContent>
          {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s} data-testid={`${tid}-service-opt-${s}`}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid={`${tid}-origin`} placeholder="Origin" value={form.origin} onChange={(e) => set("origin", e.target.value)} className="bg-background/50 h-12" />
        <Input data-testid={`${tid}-destination`} placeholder="Destination" value={form.destination} onChange={(e) => set("destination", e.target.value)} className="bg-background/50 h-12" />
      </div>
      <Input data-testid={`${tid}-date`} type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="bg-background/50 h-12" />
      <Textarea data-testid={`${tid}-details`} placeholder="Cargo details, weight, temperature..." value={form.details} onChange={(e) => set("details", e.target.value)} className="bg-background/50 min-h-[100px]" />
      <button type="submit" disabled={loading} data-testid={`${tid}-submit`}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-4 hover:shadow-[0_0_40px_hsl(var(--primary)/0.5)] transition-shadow disabled:opacity-60">
        {loading ? "Sending..." : "Request My Quote"} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}
